// ImageLightbox — full-screen image viewer with pinch/wheel zoom, pan, keyboard + swipe navigation
// Zero external deps. Works on desktop (mouse wheel + drag) and mobile (pinch + pan + swipe).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCcw, Download } from "lucide-react";

const MIN_SCALE = 1;
const MAX_SCALE = 6;

/**
 * Props:
 * - open: boolean
 * - onClose: () => void
 * - images: [{ url, thumb_url?, alt? }, ...] — full-res `url` is preferred; falls back to thumb_url.
 * - startIndex: index to open at
 * - resolveSrc: optional (img) => string   — customise how to convert an image entry to a URL
 *                                          (defaults to img.url || img.thumb_url)
 */
export default function ImageLightbox({ open, onClose, images = [], startIndex = 0, resolveSrc }) {
  const [index, setIndex] = useState(startIndex);
  const [scale, setScale] = useState(1);
  const [tx, setTx] = useState(0);
  const [ty, setTy] = useState(0);
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const pinch = useRef({ active: false, dist0: 0, scale0: 1, cx: 0, cy: 0 });
  const pan = useRef({ active: false, x0: 0, y0: 0, tx0: 0, ty0: 0, moved: false });
  const swipe = useRef({ x0: 0, active: false });

  const src = useMemo(() => {
    const img = images[index];
    if (!img) return "";
    if (resolveSrc) return resolveSrc(img);
    return img.url || img.thumb_url || "";
  }, [images, index, resolveSrc]);

  const reset = useCallback(() => { setScale(1); setTx(0); setTy(0); }, []);

  const next = useCallback(() => {
    if (index < images.length - 1) { setIndex(index + 1); reset(); }
  }, [index, images.length, reset]);
  const prev = useCallback(() => {
    if (index > 0) { setIndex(index - 1); reset(); }
  }, [index, reset]);

  // Reset when opening / images change
  useEffect(() => { if (open) { setIndex(startIndex); reset(); } }, [open, startIndex, reset]);

  // Keyboard
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "+" || e.key === "=") setScale((s) => Math.min(MAX_SCALE, s * 1.25));
      else if (e.key === "-") setScale((s) => Math.max(MIN_SCALE, s / 1.25));
      else if (e.key === "0") reset();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose, next, prev, reset]);

  // Wheel zoom (desktop)
  const onWheel = (e) => {
    if (!containerRef.current) return;
    e.preventDefault();
    const rect = containerRef.current.getBoundingClientRect();
    const cx = e.clientX - rect.left - rect.width / 2;
    const cy = e.clientY - rect.top - rect.height / 2;
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    setScale((s) => {
      const next_ = Math.max(MIN_SCALE, Math.min(MAX_SCALE, s * factor));
      const ratio = next_ / s;
      setTx((t) => t + (cx - t) * (1 - ratio));
      setTy((t) => t + (cy - t) * (1 - ratio));
      return next_;
    });
  };

  // Pointer gestures: single-pointer = pan (zoomed) or swipe (fit), two-pointer = pinch
  const activePointers = useRef(new Map());
  const onPointerDown = (e) => {
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    e.currentTarget.setPointerCapture(e.pointerId);
    if (activePointers.current.size === 2) {
      const [a, b] = [...activePointers.current.values()];
      pinch.current.active = true;
      pinch.current.dist0 = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      pinch.current.scale0 = scale;
      const rect = containerRef.current.getBoundingClientRect();
      pinch.current.cx = (a.x + b.x) / 2 - rect.left - rect.width / 2;
      pinch.current.cy = (a.y + b.y) / 2 - rect.top - rect.height / 2;
      pan.current.active = false;
      swipe.current.active = false;
    } else if (activePointers.current.size === 1) {
      pan.current.active = true;
      pan.current.x0 = e.clientX;
      pan.current.y0 = e.clientY;
      pan.current.tx0 = tx;
      pan.current.ty0 = ty;
      pan.current.moved = false;
      swipe.current.active = scale === 1;
      swipe.current.x0 = e.clientX;
    }
  };
  const onPointerMove = (e) => {
    if (!activePointers.current.has(e.pointerId)) return;
    activePointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch.current.active && activePointers.current.size >= 2) {
      const [a, b] = [...activePointers.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const next_ = Math.max(MIN_SCALE, Math.min(MAX_SCALE, pinch.current.scale0 * (dist / pinch.current.dist0)));
      setScale(next_);
      return;
    }
    if (pan.current.active && activePointers.current.size === 1) {
      const dx = e.clientX - pan.current.x0;
      const dy = e.clientY - pan.current.y0;
      if (Math.abs(dx) + Math.abs(dy) > 4) pan.current.moved = true;
      if (scale > 1) {
        setTx(pan.current.tx0 + dx);
        setTy(pan.current.ty0 + dy);
      }
    }
  };
  const onPointerUp = (e) => {
    const pointer = activePointers.current.get(e.pointerId);
    activePointers.current.delete(e.pointerId);
    if (activePointers.current.size < 2) pinch.current.active = false;
    if (activePointers.current.size === 0) {
      // Swipe navigation only when at fit (scale=1) and it wasn't a real pan
      if (swipe.current.active && scale === 1 && pointer) {
        const dx = pointer.x - swipe.current.x0;
        if (Math.abs(dx) > 60) {
          if (dx < 0) next(); else prev();
        }
      }
      pan.current.active = false;
      swipe.current.active = false;
    }
  };

  const zoomIn = () => setScale((s) => Math.min(MAX_SCALE, s * 1.25));
  const zoomOut = () => setScale((s) => Math.max(MIN_SCALE, s / 1.25));

  if (!open || !images.length) return null;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center select-none touch-none"
      onWheel={onWheel}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      role="dialog"
      aria-modal="true"
      data-testid="image-lightbox"
    >
      {/* Backdrop click closes (only when not panning) */}
      <div className="absolute inset-0" onClick={(e) => { if (!pan.current.moved) onClose(); e.stopPropagation(); }}/>

      {/* Toolbar */}
      <div className="absolute top-3 right-3 z-10 flex gap-2">
        <IconBtn label="Zoom out" onClick={zoomOut} disabled={scale <= MIN_SCALE + 0.001}><ZoomOut size={16}/></IconBtn>
        <IconBtn label="Reset" onClick={reset}><RotateCcw size={16}/></IconBtn>
        <IconBtn label="Zoom in" onClick={zoomIn} disabled={scale >= MAX_SCALE - 0.001}><ZoomIn size={16}/></IconBtn>
        <a href={src} download target="_blank" rel="noreferrer"
           onClick={(e) => e.stopPropagation()}
           className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-sm"
           title="Download" data-testid="lightbox-download">
          <Download size={16}/>
        </a>
        <IconBtn label="Close" onClick={onClose} testid="lightbox-close"><X size={18}/></IconBtn>
      </div>

      {/* Counter + caption */}
      {images.length > 1 && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10 text-white/80 text-xs uppercase tracking-widest px-3 py-1 rounded-full bg-white/10 backdrop-blur-sm" data-testid="lightbox-counter">
          {index + 1} / {images.length}
        </div>
      )}

      {/* Prev / next */}
      {images.length > 1 && index > 0 && (
        <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-sm" data-testid="lightbox-prev" aria-label="Previous">
          <ChevronLeft size={22}/>
        </button>
      )}
      {images.length > 1 && index < images.length - 1 && (
        <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur-sm" data-testid="lightbox-next" aria-label="Next">
          <ChevronRight size={22}/>
        </button>
      )}

      {/* Image */}
      <img
        ref={imgRef}
        src={src}
        alt={images[index]?.alt || ""}
        draggable={false}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => { e.stopPropagation(); setScale((s) => s > 1 ? 1 : 2.5); if (scale > 1) reset(); }}
        className="relative z-[5] max-w-[95vw] max-h-[92vh] rounded-lg shadow-2xl will-change-transform"
        style={{
          transform: `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`,
          transition: pinch.current.active || pan.current.active ? "none" : "transform 120ms ease-out",
          cursor: scale > 1 ? (pan.current.active ? "grabbing" : "grab") : "zoom-in",
        }}
      />

      {/* Bottom hint */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-white/60 text-[10px] uppercase tracking-widest hidden md:block">
        Scroll / pinch to zoom · Drag to pan · Arrow keys navigate · Esc closes
      </div>
    </div>
  );
}

function IconBtn({ children, onClick, disabled, label, testid }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); if (!disabled) onClick(); }}
      className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 text-white flex items-center justify-center backdrop-blur-sm"
      aria-label={label}
      title={label}
      data-testid={testid}
    >
      {children}
    </button>
  );
}
